import { limit } from "firebase/firestore";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../api/firebase/references";
import { HealthCareFacilityModel } from "../../../../../data-model/healthFacility";
import { NccnIcdConfig } from "../../../../../data-model/nccnIcdConfig";

export interface SourceOrg {
  id: string;
  name: string;
}

/**
 * Find facilities that already have at least one per-org dx_code_config doc.
 *
 * Probes each org's `dx_code_config` subcollection with a `limit(1)` read so
 * listing source orgs stays cheap (one doc read per org, not a full fetch).
 * A probe that errors (network, permissions) is treated as "no config" rather
 * than failing the whole list.
 *
 * @param facilities All facilities from the healthFacility slice.
 * @param excludeOrgId The currently selected org, which is never listed as a source.
 * @return Orgs (unique by DocId, in facility-list order) that have a config.
 */
export const findOrgsWithDxCodeConfig = async (
  facilities: HealthCareFacilityModel[],
  excludeOrgId: string,
): Promise<SourceOrg[]> => {
  const uniqueOrgs = new Map<string, string>();
  facilities.forEach((facility) => {
    const id = facility.DocId?.trim();
    if (!id || id === excludeOrgId || uniqueOrgs.has(id)) return;
    uniqueOrgs.set(id, facility.Name ?? "");
  });

  const candidates = Array.from(uniqueOrgs.entries());

  const results = await Promise.allSettled(
    candidates.map(async ([orgId]) => {
      const docs =
        await FirestoreService.getDocumentsWithConstraints<NccnIcdConfig>(
          FirestoreCollectionReference.nccnIcdConfig(orgId),
          [limit(1)],
        );
      return { orgId, hasConfig: docs.length > 0 };
    }),
  );

  return candidates
    .filter((_, index) => {
      const result = results[index];
      return result.status === "fulfilled" && result.value.hasConfig;
    })
    .map(([orgId, name]) => ({ id: orgId, name }));
};

/**
 * Clone every per-org dx_code_config doc from one org into another, plus the
 * source org's dx_source_policy map.
 *
 * Invariant: the caller only invokes this when the target org has zero
 * dx_code_config docs (the R4 "Copy + import" empty state), so writing with
 * setDoc-by-id cannot clobber an existing target config.
 *
 * Each doc is written with its source id (the J-code) so the engine's
 * per-jcode resolution keeps working in the target org.
 *
 * @throws on any Firestore failure — the target is left partially written only
 *         if a write mid-way fails; callers surface a generic error toast.
 */
export const copyDxCodeConfig = async (
  sourceOrgId: string,
  targetOrgId: string,
  sourceSourcePolicy: Record<string, boolean> | undefined,
): Promise<{ copiedDocs: number }> => {
  const sourceDocs = await FirestoreService.getAllDocuments<NccnIcdConfig>(
    FirestoreCollectionReference.nccnIcdConfig(sourceOrgId),
  );

  const targetCollection =
    FirestoreCollectionReference.nccnIcdConfig(targetOrgId);
  for (const config of sourceDocs) {
    // Keep the source doc's id (the J-code) so per-jcode resolution is
    // preserved in the target org.
    await FirestoreService.addDocumentWithId<NccnIcdConfig>(targetCollection, {
      ...config,
    } as unknown as NccnIcdConfig);
  }

  if (sourceSourcePolicy && Object.keys(sourceSourcePolicy).length > 0) {
    await FirestoreService.updateDocument(
      FirestoreCollectionReference.healthcareFacility(),
      targetOrgId,
      { dx_source_policy: sourceSourcePolicy },
    );
  }

  return { copiedDocs: sourceDocs.length };
};
